import React, { useState } from 'react';
import {
  View, StyleSheet, Text, ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Auth } from '@aws-amplify/auth';
import Colors from '../../constants/Colors';
import CustomTextField from '../CustomTextField';
import Button from '../Button';
import AlertModal from '../AlertModal';
import isMobile from '../IsMobile';
import { logEvent } from '../LogEvent';
import TickIcon12 from '../12pxIcons/TickIcon12';
import Style from '../../constants/Style';
import DeleteIcon16 from '../16pxIcons/DeleteIcon16';
import PressableStyled from '../PressableStyled';

const passwordRequirement = (text) => <View style={styles.requirement}>
  {!isMobile && <View style={styles.bulletPoint} />}
  <Text style={styles.requirementText} selectable>
    {text}
  </Text>
</View>;

const ChangePassword = () => {
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [passwordModalVisible, setPasswordModalVisible] = useState<boolean>(
    false,
  );
  const [passwordModalDesc, setPasswordModalDesc] = useState<string>('');
  const [passwordModalTitle, setPasswordModalTitle] = useState<string>('');
  const [passwordModalError, setPasswordModalError] = useState<boolean>(false);
  const [changedSuccess, setChangedSuccess] = useState<boolean>(false);

  return <><ScrollView
    style={styles.container}
    contentContainerStyle={styles.content}
  >
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'position' : null} keyboardVerticalOffset={150}>
      <View style={styles.mainContent}>
        <View style={styles.fieldContainer}>
          <CustomTextField
            password
            title={'Current password'}
            placeholder='Enter current password'
            value={oldPassword}
            updateValue={setOldPassword}
          />
          <CustomTextField
            password
            title={'New password'}
            placeholder='Create new password'
            value={newPassword}
            updateValue={setNewPassword}
          />
          <CustomTextField
            password
            title={'Confirm new password'}
            placeholder='Confirm new password'
            value={confirmNewPassword}
            updateValue={setConfirmNewPassword}
            errorText='Passwords must match!'
            invalid={confirmNewPassword.length > 0
              && confirmNewPassword !== newPassword}
          />
        </View>
        <View
          style={styles.requirementsSection}
          testID='password-parameters-label'
        >
          <Text style={styles.requirementsTitle}>
            {'Password must have:'}
          </Text>
          {isMobile
            ? passwordRequirement('At least 8 characters, 1 upper and lower case, 1 number, and one special character')
            : <>
              {passwordRequirement('At least 8 characters')}
              {passwordRequirement('At least 1 uppercase character')}
              {passwordRequirement('At least 1 lowercase character')}
              {passwordRequirement('At least 1 numeric character')}
              {passwordRequirement('At least 1 special character')}
            </>}
        </View>
      </View>
      <Button
        title='Save'
        testID='save-password-btn'
        affirmative
        style={styles.saveButton}
        disabled={!(newPassword.length >= 8 && confirmNewPassword.length >= 8
          && oldPassword.length > 0 && newPassword === confirmNewPassword)}
        onPress={() => {
          logEvent('ChangePassword', 'Save_password');
          Auth.currentAuthenticatedUser()
            // eslint-disable-next-line max-len
            .then((user) => Auth
              .changePassword(user, oldPassword, newPassword)
              .then((data) => {
                setPasswordModalTitle('Success!');
                setPasswordModalDesc(JSON.stringify(data));
                setChangedSuccess(true);
                setNewPassword('');
                setOldPassword('');
                setConfirmNewPassword('');
              }))
            .catch((err) => {
              setPasswordModalTitle('Something went wrong!');
              setPasswordModalDesc(err?.message
                ?? JSON.stringify(err));
              setPasswordModalError(true);
              setPasswordModalVisible(true);
            });
        }}
      />
      <AlertModal
        modalVisible={passwordModalVisible}
        title={passwordModalTitle}
        message={passwordModalDesc}
        error={passwordModalError}
        setModalVisible={setPasswordModalVisible}
      />
    </KeyboardAvoidingView>
  </ScrollView>
    {changedSuccess ? <View
      style={[styles.changeSuccessDialog, Style.boxShadow]}
      testID='success-banner'
    >
      <View style={styles.check}>
        <TickIcon12 />
      </View>
      <Text style={styles.pwChangedText}>{'Password changed'}</Text>
      <PressableStyled onPress={() => setChangedSuccess(false)}>
        <DeleteIcon16 />
      </PressableStyled>
    </View> : null}
  </>;
};

const styles = StyleSheet.create({
  container: {
    margin: 24,
    marginBottom: 0,
    borderRadius: 24,
    backgroundColor: '#FFF',
  },
  content: {
    flexGrow: 1,
    padding: isMobile ? 20 : 0,
  },
  mainContent: {
    flexDirection: isMobile ? 'column-reverse' : 'row',
  },
  fieldContainer: {
    flex: 1,
    maxWidth: 400,
    marginEnd: isMobile ? undefined : 58,
  },
  requirementsSection: {
    backgroundColor: Colors.light.hereworksWhite200,
    padding: 16,
    borderColor: Colors.light.hereworksWhite500,
    borderRadius: 16,
    flex: isMobile ? undefined : 1,
    maxWidth: 400,
  },
  requirementsTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: isMobile ? 16 : 18,
    lineHeight: isMobile ? 24 : 27,
    color: Colors.light.hereworksBlack600,
    marginBottom: 12,
  },
  requirement: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4,
  },
  bulletPoint: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.light.hereworksBlue500,
  },
  requirementText: {
    marginLeft: 8,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 16,
    color: Colors.light.hereworksBlack400,
  },
  saveButton: {
    marginTop: 40,
    flex: undefined,
    alignSelf: 'flex-start',
  },
  changeSuccessDialog: {
    borderWidth: 1,
    flexDirection: 'row',
    borderColor: Colors.light.hereworksGreen500,
    borderRadius: 12,
    position: 'absolute',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 16,
    top: -74,
    left: 20,
    width: 556,
  },
  check: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.light.hereworksGreen500,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pwChangedText: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksGreen500,
    fontFamily: 'Poppins_500Medium',
    marginLeft: 16,
    flex: 1,
  },
});

export default ChangePassword;
